import { useEffect, useState, useCallback } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { money } from "./shared";
import { useI18n } from "../../../context/I18nContext";

export default function Reportes({ companyId, year, month }) {
  const { t } = useI18n();
  const [view, setView] = useState("anual");
  const [annual, setAnnual] = useState(null);
  const [sat, setSat] = useState(null);
  const [dl, setDl] = useState(false);

  const downloadWorksheet = async () => {
    const yr = year || new Date().getFullYear();
    const m = month || new Date().getMonth() + 1;
    setDl(true);
    try {
      const r = await api.get(`/accounting/reports/worksheet.pdf?company_id=${companyId}&year=${yr}&month=${m}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `papel_trabajo_${companyId}_${yr}_${String(m).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error(t("os.contab.worksheetErr")); }
    finally { setDl(false); }
  };

  const load = useCallback(() => {
    if (!companyId) return;
    const yr = year || new Date().getFullYear();
    api.get(`/accounting/reports/annual?company_id=${companyId}&year=${yr}`).then((r) => setAnnual(r.data)).catch(() => {});
    const m = month || new Date().getMonth() + 1;
    api.get(`/accounting/reports/sat?company_id=${companyId}&year=${yr}&month=${m}`).then((r) => setSat(r.data)).catch(() => {});
  }, [companyId, year, month]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 bg-white border border-navy/10 rounded-lg p-1 w-fit">
          {["anual", "sat"].map((v) => (
            <button key={v} data-testid={`report-tab-${v}`} onClick={() => setView(v)}
              className={`text-xs px-4 py-2 rounded-md transition-colors ${view === v ? "bg-navy text-cream" : "text-navy/50 hover:text-navy"}`}>{t(`os.contab.reportViews.${v}`)}</button>
          ))}
        </div>
        <button data-testid="report-download-worksheet" onClick={downloadWorksheet} disabled={dl}
          className="inline-flex items-center gap-2 rounded-full border border-violet text-violet text-sm px-4 py-2.5 hover:bg-violet/5 transition-colors disabled:opacity-60">
          {dl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("os.contab.worksheetPdf")}
        </button>
      </div>

      {view === "anual" && annual && (
        <div className="space-y-5" data-testid="report-annual">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["ingresos", annual.totals.ingreso, "text-emerald-600"], ["egresos", annual.totals.egreso, "text-destructive"], ["nomina", annual.totals.nomina, "text-violet"], ["utilidad", annual.totals.utilidad, "text-navy"]].map(([k, v, c]) => (
              <div key={k} className="bg-white border border-navy/10 rounded-lg p-5"><div className="overline text-navy/40 text-[0.6rem]">{t(`os.contab.annualCols.${k}`)} {annual.year}</div><div className={`mt-2 text-2xl font-medium ${c}`}>{money(v)}</div></div>
            ))}
          </div>
          <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto">
            <div className="grid grid-cols-[110px_repeat(5,1fr)] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10 min-w-[720px]">
              <span>{t("os.contab.annualCols.mes")}</span><span>{t("os.contab.annualCols.ingresos")}</span><span>{t("os.contab.annualCols.egresos")}</span><span>{t("os.contab.annualCols.nomina")}</span><span>{t("os.contab.annualCols.utilidad")}</span><span>{t("os.contab.annualCols.ivaPorPagar")}</span></div>
            {annual.months.map((m) => (
              <div key={m.month} data-testid={`report-month-${m.month}`} className="grid grid-cols-[110px_repeat(5,1fr)] gap-3 px-5 py-3 items-center border-b border-navy/5 last:border-0 text-sm min-w-[720px]">
                <span className="text-navy/60">{m.label}</span>
                <span className="text-emerald-600">{money(m.ingreso)}</span>
                <span className="text-destructive">{money(m.egreso)}</span>
                <span className="text-violet">{money(m.nomina)}</span>
                <span className="font-medium">{money(m.utilidad)}</span>
                <span>{money(m.iva_trasladado - m.iva_acreditable)}</span>
              </div>
            ))}
            <div className="grid grid-cols-[110px_repeat(5,1fr)] gap-3 px-5 py-3 items-center text-sm font-medium bg-navy/[0.03] min-w-[720px]">
              <span>{t("os.contab.total")}</span><span className="text-emerald-600">{money(annual.totals.ingreso)}</span><span className="text-destructive">{money(annual.totals.egreso)}</span>
              <span className="text-violet">{money(annual.totals.nomina)}</span><span>{money(annual.totals.utilidad)}</span><span>{money(annual.totals.iva_por_pagar)}</span>
            </div>
          </div>
        </div>
      )}

      {view === "sat" && sat && (
        <div className="space-y-5" data-testid="report-sat">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h3 className="text-lg font-medium">{sat.company.name} · {sat.periodo}</h3>
            <span className="text-xs text-navy/40">{sat.regimen}</span>
          </div>

          <div className="bg-white border border-navy/10 rounded-lg p-5">
            <div className="overline text-navy/40 mb-3">{t("os.contab.ivaTitle")}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-navy/40 text-xs">{t("os.contab.ivaCobrado")}</div><div className="font-medium mt-1">{money(sat.iva_mensual.iva_cobrado_trasladado)}</div></div>
              <div><div className="text-navy/40 text-xs">{t("os.contab.ivaPagado")}</div><div className="font-medium mt-1">{money(sat.iva_mensual.iva_pagado_acreditable)}</div></div>
              <div><div className="text-navy/40 text-xs">{t("os.contab.ivaRetenido")}</div><div className="font-medium mt-1">{money(sat.iva_mensual.iva_retenido)}</div></div>
              <div><div className="text-navy/40 text-xs">{t("os.contab.resultado")} ({sat.iva_mensual.a_favor_o_cargo})</div><div className={`font-medium mt-1 ${sat.iva_mensual.resultado >= 0 ? "text-destructive" : "text-emerald-600"}`}>{money(Math.abs(sat.iva_mensual.resultado))}</div></div>
            </div>
          </div>

          <div className="bg-white border border-navy/10 rounded-lg p-5">
            <div className="overline text-navy/40 mb-3">{t("os.contab.isrTitle")}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-navy/40 text-xs">{t("os.contab.ingresosMes")}</div><div className="font-medium mt-1">{money(sat.isr_provisional.ingresos_nominales_mes)}</div></div>
              <div><div className="text-navy/40 text-xs">{t("os.contab.ingresosAcum")}</div><div className="font-medium mt-1">{money(sat.isr_provisional.ingresos_acumulados_ejercicio)}</div></div>
              <div><div className="text-navy/40 text-xs">{t("os.contab.deduccionesAcum")}</div><div className="font-medium mt-1">{money(sat.isr_provisional.deducciones_acumuladas)}</div></div>
              <div><div className="text-navy/40 text-xs">{t("os.contab.isrRetenidoAcum")}</div><div className="font-medium mt-1">{money(sat.isr_provisional.isr_retenido_acumulado)}</div></div>
            </div>
            <p className="mt-3 text-[11px] text-navy/40">{sat.isr_provisional.nota}</p>
          </div>

          <p className="text-[11px] text-navy/40">{sat.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
